"use client";

/**
 * The comparison board - ONE surface for the whole side-by-side.
 *
 * Ground-up replacement for the old three-way split (cards row +
 * mobile scoreboard + sectioned accordion table). Those three re-
 * established the listing identity three separate times and nested
 * card-in-card padding until phones showed more chrome than data.
 *
 * Here the mental model is a spec sheet: COLUMNS are the listings
 * (photo, price, verdict live once, in the column header), ROWS are
 * the facts, grouped by quiet full-width dividers instead of nested
 * cards. One component serves every viewport: the label column is
 * sticky and the data columns scroll horizontally on phones.
 *
 * Highlight rule (deliberately single-colored): the BEST cell per
 * rankable row gets an emerald tint - nothing is painted red. Rows
 * where no listing has data are dropped entirely.
 */
import { signedPct } from "@/lib/format/change";
import { LINK } from "@/lib/ui-classes";
import Link from "@/lib/localized-link";
import { useT, useLocale } from "@/lib/locale-context";
import { formatPrice } from "@/lib/api";
import { roundEstimate } from "@/lib/estimate";
import type { AnalysisResult } from "@/types";
import { translateVerdict, type TranslationKey } from "@/lib/i18n";
import { colorForIndex } from "./palette";
import {
  bestAndWorstIndex,
  computeHeadlineWinners,
  pricePerM2,
  redFlagCount,
} from "./metrics";
import { resolveBand, formatBand, ownComplexDelta } from "./confidence";
import { Home, Trophy, Target, ExternalLink } from "lucide-react";
import Photo from "@/components/Photo";

interface Props {
  results: AnalysisResult[];
  /** 1-based index from the compare response ("best for your intent"). */
  bestForIntentIndex?: number | null;
}

// Explicit dark: variants: the theme's palette remap keeps the -100
// tints glaring on dark surfaces ("Çox bahalı" read as a neon slab);
// translucent tint + light text is the app's dark-chip idiom.
const VERDICT_CLASSES: Record<string, string> = {
  "Exceptional Deal": "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  "Great Deal": "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  "Good Deal": "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  "Fair Price": "bg-gray-100 text-gray-700 dark:bg-white/10",
  "Slightly Overpriced": "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  Overpriced: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
  "Heavily Overpriced": "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  "Suspiciously Low": "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-400",
  "Luxury Segment": "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-400",
  "Needs Verification": "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
  "Suspiciously High": "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
};

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  return signedPct(n, 1);
}

function fmtKm(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  return `${n.toFixed(1)} km`;
}

function cf(r: AnalysisResult, key: string): number | null {
  const features = (r as unknown as { computed_features?: Record<string, number> })
    .computed_features;
  const v = features?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

interface RowSpec {
  labelKey: TranslationKey;
  /** Ranking series; null entries don't compete. */
  values: Array<number | null>;
  display: React.ReactNode[];
  /** "min" | "max" tints the best cell emerald; null = informational. */
  best: "min" | "max" | null;
}

function isEmpty(d: React.ReactNode, na?: string): boolean {
  return d == null || d === "-" || d === "" || (na !== undefined && d === na);
}

export default function CompareBoard({ results, bestForIntentIndex }: Props) {
  const t = useT();
  const { locale } = useLocale();
  // Localized missing-value placeholder. Rendered in cells AND treated
  // as "empty" by isEmpty() so all-missing rows still get suppressed.
  const na = t("compare.table.na");
  const orNa = (s: string) => (s === "-" ? na : s);
  if (results.length === 0) return null;

  const isRent = results[0]?.analysis_mode === "rent";
  const priceUnit = isRent ? t("result.permonth") : "AZN";
  const bandGrain = isRent ? 10 : 1000;
  const roundBand = (n: number) => Math.round(n / bandGrain) * bandGrain;
  const confTint = (level: "high" | "medium" | "low"): string =>
    level === "high"
      ? "bg-emerald-50 text-emerald-700 dark:text-emerald-300"
      : level === "medium"
        ? "bg-amber-50 text-amber-700 dark:text-amber-400"
        : "bg-rose-50 text-rose-700 dark:text-rose-300";

  const winnerIndex = computeHeadlineWinners(results).bestOverall;
  const intentIdx =
    typeof bestForIntentIndex === "number" && bestForIntentIndex >= 1
      ? bestForIntentIndex - 1
      : null;

  const num = (v: number | null | undefined): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;

  // ── Row groups ────────────────────────────────────────────────────
  const groups: { titleKey: TranslationKey; rows: RowSpec[] }[] = [
    {
      titleKey: "compare.table.price_section",
      rows: [
        {
          labelKey: "compare.table.row.asking_price",
          values: results.map((r) => num(r.price)),
          display: results.map((r) => `${formatPrice(r.price)} ${priceUnit}`),
          best: null, // cheap != good; the deal signal is Bazardan %
        },
        {
          labelKey: "compare.table.row.fair_value",
          values: results.map((r) =>
            num(r.adjusted_predicted_price ?? r.predicted_price)),
          display: results.map((r) => {
            const v = r.adjusted_predicted_price ?? r.predicted_price;
            if (!v) return na;
            const band = resolveBand(r);
            return (
              <span className="flex flex-col leading-tight gap-0.5">
                <span>{formatPrice(roundEstimate(v, isRent))} {priceUnit}</span>
                {band && (
                  <span className="text-xs text-gray-500 font-normal">
                    {formatBand(band, formatPrice, roundBand)}
                  </span>
                )}
                {band && band.confidencePct != null && (
                  <span
                    className={`mt-0.5 inline-flex w-fit items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold ${confTint(band.level)}`}
                  >
                    {band.confidencePct}%{" "}
                    {t(`confidence.label.${band.level}` as TranslationKey)}
                  </span>
                )}
              </span>
            );
          }),
          best: null,
        },
        {
          labelKey: "compare.table.row.overpriced_pct",
          values: results.map((r) => num(r.overpriced_pct)),
          display: results.map((r) => orNa(fmtPct(r.overpriced_pct))),
          best: "min",
        },
        {
          labelKey: "compare.table.row.notariat",
          values: results.map((r) => num(r.notariat_estimate?.total_price)),
          display: results.map((r) => {
            const v = r.notariat_estimate?.total_price;
            return v ? `${formatPrice(v)} ${priceUnit}` : na;
          }),
          best: null,
        },
        {
          labelKey: "compare.table.row.notariat_delta",
          values: results.map((r) => {
            const nv = r.notariat_estimate?.total_price;
            return nv && r.price ? ((r.price - nv) / nv) * 100 : null;
          }),
          display: results.map((r) => {
            const nv = r.notariat_estimate?.total_price;
            if (!nv || !r.price) return na;
            return fmtPct(((r.price - nv) / nv) * 100);
          }),
          best: "min",
        },
        {
          labelKey: "compare.table.row.vs_own_complex",
          values: results.map((r) => num(ownComplexDelta(r)?.pct)),
          display: results.map((r) => {
            const own = ownComplexDelta(r);
            if (!own) return na;
            const tone = own.pct < -0.5
              ? "text-emerald-700 dark:text-emerald-400"
              : own.pct > 0.5 ? "text-amber-700 dark:text-amber-400" : "text-gray-600";
            return (
              <span className="flex flex-col leading-tight">
                <span className={`font-semibold ${tone}`}>{fmtPct(own.pct)}</span>
                <span className="truncate text-xs font-normal text-gray-500">
                  {own.name}
                </span>
              </span>
            );
          }),
          best: "min",
        },
        {
          labelKey: "compare.table.row.savings",
          values: results.map((r) => (r.savings > 0 ? r.savings : null)),
          display: results.map((r) =>
            r.savings > 0 ? `${formatPrice(r.savings)} ${priceUnit}` : na),
          best: "max",
        },
        {
          labelKey: "compare.table.row.ppm",
          values: results.map((r) => num(pricePerM2(r))),
          display: results.map((r) => {
            const ppm = pricePerM2(r);
            return ppm ? `${Math.round(ppm).toLocaleString()} AZN/m²` : na;
          }),
          best: "min",
        },
      ],
    },
    {
      titleKey: "compare.table.specs_section",
      rows: [
        {
          labelKey: "compare.table.row.area",
          values: results.map((r) => num(r.area_m2)),
          display: results.map((r) =>
            r.area_m2 != null ? `${Math.round(r.area_m2)} m²` : na),
          best: "max",
        },
        {
          labelKey: "compare.table.row.rooms",
          values: results.map((r) => num(r.room_count)),
          display: results.map((r) =>
            r.room_count != null ? String(r.room_count) : na),
          best: null, // more rooms isn't objectively better
        },
        {
          labelKey: "compare.table.row.floor",
          values: results.map(() => null),
          display: results.map((r) =>
            r.floor != null && r.total_floors != null
              ? `${r.floor} / ${r.total_floors}`
              : na),
          best: null,
        },
        {
          labelKey: "compare.table.row.category",
          values: results.map(() => null),
          display: results.map((r) => r.category_name || na),
          best: null,
        },
        {
          labelKey: "compare.table.row.repair",
          values: results.map(() => null),
          display: results.map((r) =>
            r.has_repair == null
              ? na
              : r.has_repair
                ? t("compare.table.yes")
                : t("compare.table.no")),
          best: null,
        },
        {
          // Photo-verified condition tier from the vision model - a
          // finer, evidence-based counterpart of the boolean above.
          // Rank on the continuous quality index (0 luxury .. 4 shell,
          // lower is better); rows suppress when nothing was scored.
          labelKey: "compare.row.photo_condition",
          values: results.map((r) =>
            num(r.photo_intel?.quality_idx != null ? -r.photo_intel.quality_idx : null)),
          display: results.map((r) =>
            r.photo_intel?.condition
              ? t(`photointel.tier.${r.photo_intel.condition}` as TranslationKey)
              : na),
          best: "max", // -quality_idx: higher = closer to luxury
        },
        {
          labelKey: "compare.table.row.kupcha",
          values: results.map(() => null),
          display: results.map((r) =>
            r.has_bill_of_sale == null
              ? na
              : r.has_bill_of_sale
                ? t("compare.table.yes")
                : t("compare.table.no")),
          best: null,
        },
      ],
    },
    {
      titleKey: "compare.table.location_section",
      rows: [
        {
          labelKey: "compare.table.row.district",
          values: results.map(() => null),
          display: results.map((r) => r.location_name || na),
          best: null,
        },
        {
          labelKey: "compare.table.row.metro",
          values: results.map((r) => cf(r, "dist_to_nearest_metro_km")),
          display: results.map((r) => {
            const km = fmtKm(cf(r, "dist_to_nearest_metro_km"));
            const nearest = r.context?.nearby_metros?.[0]?.name;
            if (km === "-") return na;
            return (
              <span className="flex flex-col leading-tight">
                <span>{km}</span>
                {nearest && (
                  <span className="text-xs font-normal text-gray-500">
                    {nearest}
                  </span>
                )}
              </span>
            );
          }),
          best: "min",
        },
        {
          labelKey: "compare.table.row.center",
          values: results.map((r) => cf(r, "dist_to_center_km")),
          display: results.map((r) => orNa(fmtKm(cf(r, "dist_to_center_km")))),
          best: "min",
        },
        {
          labelKey: "compare.table.row.sea",
          values: results.map((r) => cf(r, "dist_to_sea_km")),
          display: results.map((r) => orNa(fmtKm(cf(r, "dist_to_sea_km")))),
          best: "min",
        },
        {
          labelKey: "compare.table.row.district_median_ppm",
          values: results.map((r) => num(r.context?.location_typical_ppm)),
          display: results.map((r) => {
            const v = r.context?.location_typical_ppm;
            return typeof v === "number"
              ? `${Math.round(v).toLocaleString()} AZN/m²`
              : na;
          }),
          best: null, // a district attribute, not a ranking
        },
      ],
    },
    {
      titleKey: "compare.table.flags_section",
      rows: [
        {
          labelKey: "compare.table.row.red_flag_count",
          values: results.map((r) => redFlagCount(r)),
          display: results.map((r) => String(redFlagCount(r))),
          best: "min",
        },
        {
          labelKey: "compare.table.row.criticality",
          values: results.map((r) => num(r.criticality)),
          display: results.map((r) =>
            typeof r.criticality === "number" ? `${r.criticality} / 5` : na),
          best: "min",
        },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      rows: g.rows.filter((row) => !row.display.every((d) => isEmpty(d, na))),
    }))
    .filter((g) => g.rows.length > 0);

  const n = results.length;
  const LABEL_PX = 96;
  const DATA_PX = 150;

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table
          className="w-full"
          style={{
            tableLayout: "fixed",
            minWidth: `${LABEL_PX + DATA_PX * n}px`,
          }}
        >
          <colgroup>
            <col style={{ width: `${LABEL_PX}px` }} />
            {results.map((_, i) => (
              <col key={i} style={{ width: `${DATA_PX}px` }} />
            ))}
          </colgroup>

          {/* ── Listing header: identity lives HERE, once ─────────── */}
          <thead>
            <tr className="border-b border-gray-200">
              <th className="sticky left-0 z-10 bg-white" aria-hidden />
              {results.map((r, i) => {
                const c = colorForIndex(i);
                const isWinner = winnerIndex === i;
                const isIntent = intentIdx === i;
                const photo = r.photo_urls?.[0];
                return (
                  <th key={i} className="px-2.5 pt-2.5 pb-3 text-left align-top font-normal">
                    <div className="relative overflow-hidden rounded-lg bg-gray-50 aspect-[16/10]">
                      {photo ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <Photo src={photo} alt="" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300">
                          <Home className="h-7 w-7" />
                        </div>
                      )}
                      {(isWinner || isIntent) && (
                        <div className="absolute right-1.5 top-1.5 flex gap-1">
                          {isWinner && (
                            <span
                              className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow"
                              title={t("compare.cards.best_overall")}
                            >
                              <Trophy className="h-3.5 w-3.5" />
                            </span>
                          )}
                          {isIntent && (
                            <span
                              className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-gray-50 shadow"
                              title={t("compare.cards.best_for_intent")}
                            >
                              <Target className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold ${c.label}`}>
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} aria-hidden />
                      {t("compare.cards.listing_label").replace("{n}", String(i + 1))}
                    </div>
                    <div className="mt-0.5 text-sm font-bold tabular-nums text-gray-900">
                      {formatPrice(r.price)}{" "}
                      <span className="text-xs font-medium text-gray-500">{priceUnit}</span>
                    </div>
                    <span
                      className={`mt-1 inline-flex w-fit rounded px-1.5 py-0.5 text-xs font-semibold ${
                        VERDICT_CLASSES[r.verdict] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {translateVerdict(r.verdict, locale)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {visibleGroups.map((g) => (
              <GroupRows key={g.titleKey} group={g} n={n} />
            ))}

            {/* Full-analysis links as the board's last row. */}
            <tr>
              <th className="sticky left-0 z-10 bg-white" aria-hidden />
              {results.map((r, i) => {
                const href = r.history_id
                  ? `/h?id=${r.history_id}`
                  : r.url
                    ? `/analyze?url=${encodeURIComponent(r.url)}`
                    : null;
                return (
                  <td key={i} className="px-2.5 py-2.5">
                    {href && (
                      <Link
                        href={href}
                        className={`inline-flex items-center gap-1 text-xs ${LINK}`}
                      >
                        {t("compare.cards.view_analysis")}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GroupRows({
  group,
  n,
}: {
  group: { titleKey: TranslationKey; rows: RowSpec[] };
  n: number;
}) {
  const t = useT();
  return (
    <>
      {/* Group divider - a quiet full-width strip, not a nested card. */}
      <tr>
        <th
          colSpan={n + 1}
          className="sticky left-0 border-y border-gray-100 bg-gray-50 px-3 py-1.5 text-left text-xs font-bold text-gray-500"
        >
          {t(group.titleKey)}
        </th>
      </tr>
      {group.rows.map((row) => {
        const { best } = bestAndWorstIndex(row.values, row.best === "min");
        const bestIdx = row.best == null ? null : best;
        return (
          <tr key={row.labelKey} className="border-b border-gray-50 last:border-0">
            <th
              scope="row"
              className="sticky left-0 z-10 bg-white py-2 pl-3 pr-2 text-left align-top text-xs font-medium leading-tight text-gray-500"
            >
              {t(row.labelKey)}
            </th>
            {row.display.map((d, i) => (
              <td
                key={i}
                className={`px-2.5 py-2 align-top text-[13px] leading-snug text-gray-900 ${
                  bestIdx === i ? "bg-emerald-50 font-semibold" : ""
                }`}
              >
                {isEmpty(d, t("compare.table.na")) ? (
                  <span className="text-gray-500">{t("compare.table.na")}</span>
                ) : (
                  d
                )}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}
